import { useMemo } from 'react';
import { useTranslation } from '@hotjar/translations';
import type {
  SurveyTemplate,
  SurveyTemplateCategory,
} from '../../domain/SurveyTemplates/SurveyTemplate';
import type { CategoryInfo } from '../common/model/Category';

export const useSurveyTemplateCategories = (
  templates: SurveyTemplate[] | undefined,
): CategoryInfo[] | undefined => {
  const { t } = useTranslation('survey-templates');

  return useMemo(() => {
    if (templates === undefined) {
      return undefined;
    }

    const templateCategories = new Map<
      string,
      { category: SurveyTemplateCategory; count: number }
    >();

    for (let i = 0; i < templates.length; i += 1) {
      const template = templates[i];
      for (let j = 0; j < template.categories.length; j += 1) {
        const category = template.categories[j];
        const existing = templateCategories.get(category.name);

        if (!existing) {
          templateCategories.set(category.name, { category, count: 1 });
        } else {
          existing.count += 1;
        }
      }
    }

    const sortedCategories = Array.from(templateCategories.values()).sort(
      (a, b) => a.category.order - b.category.order,
    );

    const allTemplatesCategory = {
      title: t('categories.all-templates'),
      count: templates.length,
      filter: () => true,
    };

    return [
      allTemplatesCategory,
      ...sortedCategories.map((entry) => ({
        title: entry.category.name,
        count: entry.count,
        filter: (template: SurveyTemplate) =>
          template.categories.some((category) => category.name === entry.category.name),
      })),
    ];
  }, [templates, t]);
};
